import { getLandingPage } from '@src/api/landingPage';
import { getSelectedProfile } from '@src/api/profiles';
import { initProfileStore } from '@src/globalStore/profileStore';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import { DATE_SHORTCUTS, DEFAULT_GLOBAL_ACCOUNT } from '@src/utils/constants';
import { get } from 'lodash';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { updateInterval, updateProfiles } from '@src/store/common/actions';
import { calcShortcutInterval, track } from '@utils';

const setMomentDefaultTimezone = (timezone) => moment.tz.setDefault(timezone);

const useProfileDefault = () => {
  const dispatch = useDispatch();
  const { setCustomerId, updateDefaultLandingPage } = useDefaultLandingPageStore();

  const {
    isFetched: isProfileFetched,
    isError: isProfileError,
    data: profileData = [],
  } = useQuery(
    ['user-profile'],
    async () => {
      const res = await getSelectedProfile();
      initProfileStore(res.data);
      return res.data;
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      notifyOnChangeProps: ['isError', 'isFetched', 'data'],
    },
  );

  const {
    isFetched: isLandingPageFetched,
    isError: isLandingPageError,
    data: landingPageData,
  } = useQuery(
    ['default-landing-page'],
    async () => {
      const res = await getLandingPage({});
      const configuration = get(res, ['data', 'configuration'], {});
      const isFromCustomerDefault = get(res, ['data', 'isFromCustomerDefault'], false);
      return { ...configuration, isFromCustomerDefault };
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      notifyOnChangeProps: ['isError', 'isFetched'],
    },
  );

  const isFetched = isProfileFetched && isLandingPageFetched;

  useMemo(() => {
    if (isFetched) {
      const profiles = Array.isArray(profileData) ? profileData : [];
      const selectedProfile = profiles.find((p) => p.is_selected);

      if (!selectedProfile) return;

      // identifier for global account (c3.Conviva-Global)
      // global account has special behavior in multiple sections
      // this flag helps do it without polluting props and params
      window.isGlobalAccount = selectedProfile.customer_id === DEFAULT_GLOBAL_ACCOUNT;

      setMomentDefaultTimezone(selectedProfile.tzName);

      setCustomerId(selectedProfile.customer_id);
      // Initialize default landing page data during app startup
      // This prevents multiple API calls when components use useDefaultLandingPageMutation
      updateDefaultLandingPage(landingPageData);

      dispatch(updateProfiles(profileData));

      /*
       * GLOBAL ACC requires default data loading time to be last24hours
       * This is a border case to be added for PII request
       */
      if (selectedProfile.customer_id == DEFAULT_GLOBAL_ACCOUNT) {
        const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.past24Hours);
        dispatch(updateInterval(from.format(), to.format(), DATE_SHORTCUTS.past24Hours, 'trends'));
      } else {
        const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.last6Hours);
        dispatch(updateInterval(from.format(), to.format(), DATE_SHORTCUTS.last6Hours, 'trends'));
      }
    }
  }, [isFetched]);

  return {
    isFetched,
    isError: isProfileError || isLandingPageError,
  };
};

export default useProfileDefault;
